import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import {
  Grid,
  Paper,
  TextField,
  Button,
  Avatar,
  Typography,
  Box,
  Divider,
  IconButton,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Alert,
  Snackbar,
  useTheme,
  alpha,
} from "@mui/material";
import {
  PhotoCamera,
  Person,
  Email,
  Phone,
  Business,
  LocationOn,
  CalendarToday,
  Edit,
  Save,
  Cancel,
  CloudUpload,
} from "@mui/icons-material";
import { setCredentials } from "../features/counter/Authslice.js"; 


const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
  hover: {
    y: -4,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const Profile = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  
  // Form state with dummy data fallback
  const [formData, setFormData] = useState({
    username: user?.username || "john_doe_2024",
    firstName: user?.firstName || "John",
    lastName: user?.lastName || "Doe",
    email: user?.email || "john.doe@example.com",
    organization: user?.organization || "Tech Solutions Inc.",
    location: user?.location || "San Francisco, CA",
    phone: user?.phone || "+1 (555) 123-4567",
    birthday: user?.birthday || "1990-05-15",
    avatar: user?.avatar || "https://i.pinimg.com/originals/3a/4b/1f/3a4b1fd903a175fe07d2b39be73336de.png",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [avatarHover, setAvatarHover] = useState(false);

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSave = () => {
    // Update Redux store
    dispatch(setCredentials({
      token: user?.token,
      user: { ...user, ...formData }
    }));
    
    setIsEditing(false);
    setShowSuccess(true);
  };

  const handleCancel = () => {
   
    setFormData({
      username: user?.username || "john_doe_2024",
      firstName: user?.firstName || "John",
      lastName: user?.lastName || "Doe",
      email: user?.email || "john.doe@example.com",
      organization: user?.organization || "Tech Solutions Inc.",
      location: user?.location || "San Francisco, CA",
      phone: user?.phone || "+1 (555) 123-4567",
      birthday: user?.birthday || "1990-05-15",
      avatar: user?.avatar || "https://i.pinimg.com/originals/3a/4b/1f/3a4b1fd903a175fe07d2b39be73336de.png",
    });
    setIsEditing(false);
  };

  const handleAvatarUpload = () => {
    
  
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Grid container spacing={3}>
        {/* Profile Picture Section */}
        <Grid item xs={12} md={4}>
          <motion.div variants={cardVariants} whileHover="hover">
            <Card
              elevation={2}
              sx={{
                height: "100%",
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.05
                )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  Profile Picture
                </Typography>

                <Box
                  sx={{ position: "relative", display: "inline-block", mb: 3 }}
                  onMouseEnter={() => setAvatarHover(true)}
                  onMouseLeave={() => setAvatarHover(false)}
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Avatar
                      src={formData.avatar}
                      sx={{
                        width: 120,
                        height: 120,
                        border: `4px solid ${theme.palette.primary.main}`,
                        boxShadow: theme.shadows[8],
                      }}
                    />
                  </motion.div>
                  
                  {avatarHover && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: "50%",
                        background: alpha(theme.palette.common.black, 0.5),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={handleAvatarUpload}
                    >
                      <PhotoCamera sx={{ color: "white", fontSize: 32 }} />
                    </motion.div>
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  JPEG or PNG no larger than 5MB
                </Typography>

                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={handleAvatarUpload}
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Upload New Image
                </Button>

                <Box sx={{ mt: 3 }}>
                  <Chip
                    label={isEditing ? "Editing Mode" : "View Mode"}
                    color={isEditing ? "primary" : "default"}
                    variant={isEditing ? "filled" : "outlined"}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        
        <Grid item xs={12} md={8}>
          <motion.div variants={cardVariants} whileHover="hover">
            <Card elevation={2} sx={{ height: "100%" }}>
              <Box
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Account Details
                </Typography>
                <IconButton
                  onClick={() => setIsEditing(!isEditing)}
                  sx={{ color: "white" }}
                >
                  <Edit />
                </IconButton>
              </Box>

              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  {/* Username */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={formData.username}
                      onChange={handleInputChange("username")}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="action" />
                          </InputAdornment>
                        ),
                      }}
                      helperText="How your name will appear to other users"
                    />
                  </Grid>

                  {/* First & Last Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.firstName}
                      onChange={handleInputChange("firstName")}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.lastName}
                      onChange={handleInputChange("lastName")}
                      disabled={!isEditing}
                    />
                  </Grid>

                  {/* Organization & Location */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Organization"
                      value={formData.organization}
                      onChange={handleInputChange("organization")}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={formData.location}
                      onChange={handleInputChange("location")}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      value={formData.email}
                      onChange={handleInputChange("email")}
                      disabled={!isEditing}
                      type="email"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Phone & Birthday */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange("phone")}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Birthday"
                      value={formData.birthday}
                      onChange={handleInputChange("birthday")}
                      disabled={!isEditing}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarToday color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  {isEditing ? (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        sx={{ textTransform: "none" }}
                      >
                        Cancel
                      </Button>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          variant="contained"
                          startIcon={<Save />}
                          onClick={handleSave}
                          sx={{
                            textTransform: "none",
                            px: 4,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          }}
                        >
                          Save Changes
                        </Button>
                      </motion.div>
                    </>
                  ) : (
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => setIsEditing(true)}
                        sx={{
                          textTransform: "none",
                          px: 4,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        }}
                      >
                        Edit Profile
                      </Button>
                    </motion.div>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          Profile updated successfully!
        </Alert>
      </Snackbar>
    </motion.div>
  );
};

export default Profile;